/**
 * Navigator Tuning · Plan detail + live tuning (feature 130). Left: the plan result per
 * specialty; right: the ranking parameters, saved to the global draft. Every ranking comes
 * from the backend sandbox (/rerank): Tuned = draft params, compared with the Baseline
 * (the plan's own config) for ▲▼/new/out; the Original is what the user saw. Non-tunable
 * plans (pre-tuning or incomplete) open read-only. Layout: design/PlanTuning.dc.html (AI generation group removed).
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  navigatorTuningKeys,
  useDebouncedValue,
  useDraftConfigVersion,
  useLiveConfigVersion,
  usePlanDetail,
  useSandbox,
  useSaveDraft,
} from '../hooks/useConfigVersions';
import { isStaleDraftError } from '../services/navigatorTuningApi';
import { fromRecorded, fromReplay } from './PlanProviderCard';
import { PlanLayout } from './PlanLayout';
import { ParametersPanel } from './TuningParametersPanel';
import type {
  ConfigVersion,
  PlanDetail,
  RankingConfigParams,
  ReplayView,
  ScoreEra,
} from '../types/navigator-tuning.type';

const DEBOUNCE_MS = 300;

const ERA_LABEL: Record<ScoreEra, string> = {
  C: 'Scores in pts (0–100)',
  B: 'Scores in legacy scale (raw sum, not comparable to pts)',
  A: 'No score breakdown recorded',
};

export function PlanDetailScreen({ sessionId }: { sessionId: string }) {
  const { data: plan, isLoading, isError } = usePlanDetail(sessionId);

  if (isLoading) return <p className="text-[#56607a]">Loading plan…</p>;
  if (isError || !plan) return <p role="alert">Failed to load the plan.</p>;
  return plan.tunable && plan.configVersion ? (
    <TunablePlan plan={plan} baseline={plan.configVersion.params} />
  ) : (
    <PlanLayout plan={plan} rowsOf={fromRecorded} bestMatchId={plan.bestMatch?.providerId}>
      <p className="rounded-md bg-[#eceef3] p-3 text-sm text-[#3d4659]">
        {plan.preTuning
          ? 'Pre-tuning plan: read-only. It was generated before tuning data was captured, so it cannot be replayed.'
          : 'Incomplete plan: read-only. Its tuning data is incomplete (no config version, a pending search or missing candidates), so it cannot be replayed.'}
        {plan.era && ` ${ERA_LABEL[plan.era]}.`}
      </p>
    </PlanLayout>
  );
}

function TunablePlan({ plan, baseline }: { plan: PlanDetail; baseline: RankingConfigParams }) {
  const queryClient = useQueryClient();
  const { data: draft, isFetched: draftFetched } = useDraftConfigVersion();
  const { data: live } = useLiveConfigVersion();
  const saveDraft = useSaveDraft();
  const [params, setParams] = useState<RankingConfigParams | null>(null);
  const [view, setView] = useState<'tuned' | 'original'>('tuned');
  const [stale, setStale] = useState(false);
  const saving = useRef<string | null>(null);
  // One save at a time: a change made while a save is in flight waits for it and is then
  // sent with the revision that save returned (else it would 409 against our own save).
  const inFlight = useRef(false);
  const queued = useRef<RankingConfigParams | null>(null);

  // Start (and re-sync after a 409) from the draft, or from the live config if there is none.
  const start = draftFetched ? (draft?.params ?? live?.params ?? null) : null;
  useEffect(() => {
    if (params === null && start) setParams(start);
  }, [params, start]);

  const debounced = useDebouncedValue(params, DEBOUNCE_MS);
  const sandbox = useSandbox(plan.sessionId, debounced);

  useEffect(() => {
    if (!debounced || !draftFetched) return;
    const key = JSON.stringify(debounced);
    if (key === JSON.stringify(draft?.params ?? live?.params) || key === saving.current) return;
    saving.current = key;
    if (inFlight.current) queued.current = debounced;
    else save(debounced, draft ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- save only when the debounced params change
  }, [debounced]);

  function save(next: RankingConfigParams, base: ConfigVersion | undefined) {
    inFlight.current = true;
    saveDraft.mutate(
      { params: next, draft: base },
      {
        onSuccess: (saved) => {
          const pending = queued.current;
          queued.current = null;
          if (pending && JSON.stringify(pending) !== JSON.stringify(saved.params))
            save(pending, saved);
          else inFlight.current = false;
        },
        onError: async (error) => {
          inFlight.current = false;
          queued.current = null;
          if (!isStaleDraftError(error)) return;
          setStale(true);
          saving.current = null;
          // Reset only once the latest draft is in the cache, or the start effect would
          // reload the stale one and the next save would overwrite the other admin's change.
          await queryClient.invalidateQueries({ queryKey: navigatorTuningKeys.draft() });
          setParams(null);
        },
      }
    );
  }

  const recalculating = sandbox.isFetching || params !== debounced;
  const result = sandbox.data;
  const shown: ReplayView | undefined =
    result && (view === 'tuned' ? result.tuned : result.original);
  const bestChanged =
    view === 'tuned' &&
    result &&
    result.tuned.bestMatch?.providerId !== result.baseline.bestMatch?.providerId;

  return (
    <PlanLayout
      plan={plan}
      rowsOf={(search) =>
        (shown?.searches.find((s) => s.searchId === search.searchId)?.candidates ?? []).map(
          fromReplay
        )
      }
      bestMatchId={shown?.bestMatch?.providerId}
      bestMatchName={shown?.bestMatch?.name}
      was={bestChanged ? (result?.baseline.bestMatch?.name ?? 'none') : undefined}
      aside={params && <ParametersPanel params={params} baseline={baseline} onChange={setParams} />}
      toolbar={
        <div className="flex flex-wrap items-center gap-3">
          {recalculating && (
            <span role="status" className="text-sm font-semibold text-bquest-green">
              Recalculating…
            </span>
          )}
          <div
            role="group"
            aria-label="Preview mode"
            className="flex gap-0.5 rounded-lg bg-[#f1ede6] p-[3px]"
          >
            {(['tuned', 'original'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={`min-h-9 rounded-md px-3.5 text-sm font-semibold ${view === v ? 'bg-white text-[#014d41] shadow-sm' : 'text-[#56607a]'}`}
              >
                {v === 'tuned' ? 'Tuned' : 'Original'}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {stale && (
        <p role="alert" className="rounded-md bg-[#fdecdc] p-3 text-sm text-[#9a3412]">
          Another admin saved the draft before you. The latest draft was reloaded; redo your change.
        </p>
      )}
      {sandbox.isError && (
        <p role="alert" className="rounded-md bg-[#fdecdc] p-3 text-sm text-[#9a3412]">
          The replay failed. Try again in a moment.
        </p>
      )}
      {result && !result.baselineMatchesOriginal && (
        <p className="rounded-md bg-[#fdf3d8] p-3 text-sm text-[#7a5a00]">
          The plan’s own config does not reproduce the Original exactly; arrows compare against the
          Baseline replay.
        </p>
      )}
    </PlanLayout>
  );
}
